import React, {useEffect, useState} from "react";
import {Checkbox, FormGroup, useMediaQuery} from "@mui/material";
import Popup from "../../components/Popup";
import {httpClient} from "../../funcs";
import {notifyAboutError, notifyUser} from "../../methods/notifyUser";
import {mobileMaxWidthMediaQuery} from "../../methods/utils";
import {
    AccessProfile,
    DEFAULT_ROLE_TEMPLATE_PERMISSIONS,
    TEMPLATE_PERMISSION_OPTIONS,
    togglePermission,
} from "../../methods/templatePermissions";
import {ReactComponent as CheckboxIcon} from "pages/Icons/size24/Checkbox.svg";
import {ReactComponent as CheckboxChecked} from "pages/Icons/size24/CheckboxChecked.svg";
import {PageLoader} from "../Contracts/ContractList/PageLoader";

const PAGE_SIZE = 10;

type TemplateMode = "default" | "custom" | "hidden" | "off" | "on";

type TemplateRow = {
    _id: string;
    name?: string;
};

function permissionCheckboxes(
    current: string[],
    onToggle: (permissionId: string) => void,
) {
    return (
        <FormGroup>
            {TEMPLATE_PERMISSION_OPTIONS.map((permission) => {
                const hasAvailable = current.includes("available");
                if (permission.id !== "available" && !hasAvailable) {
                    return null;
                }
                return (
                    <div
                        className="titleContainer"
                        key={permission.id}
                        style={{marginTop: "10px"}}
                    >
                        <div>
                            <div className="Subheader_17">{permission.label}</div>
                            <div className="commonSubtitle">{permission.description}</div>
                        </div>
                        <Checkbox
                            icon={<CheckboxIcon className="icon"/>}
                            checkedIcon={<CheckboxChecked className="icon"/>}
                            checked={current.includes(permission.id)}
                            onChange={() => onToggle(permission.id)}
                        />
                    </div>
                );
            })}
        </FormGroup>
    );
}

function AccessProfilePopup(props: {
    profile?: AccessProfile | null;
    onClose: () => void;
    onSaved: () => void;
    $store?: any;
    $commitToStore?: (data: any) => void;
}) {
    const mobile = useMediaQuery(mobileMaxWidthMediaQuery);
    const editing = Boolean(props.profile?._id);
    const [name, setName] = useState(props.profile?.name || "");
    const [scope, setScope] = useState<"all" | "selected">(
        props.profile?.scope === "selected" ? "selected" : "all",
    );
    const [defaultPermissions, setDefaultPermissions] = useState<string[]>(
        props.profile?.default_permissions
            ? [...props.profile.default_permissions]
            : [...DEFAULT_ROLE_TEMPLATE_PERMISSIONS],
    );
    const [modes, setModes] = useState<{[templateId: string]: TemplateMode}>({});
    const [overrides, setOverrides] = useState<{[templateId: string]: string[]}>({});
    const [templates, setTemplates] = useState<TemplateRow[]>([]);
    const [hasMoreTemplates, setHasMoreTemplates] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const stored = props.profile?.templates || {};
        const nextModes: {[id: string]: TemplateMode} = {};
        const nextOverrides: {[id: string]: string[]} = {};
        const selected = props.profile?.scope === "selected";
        Object.keys(stored).forEach((templateId) => {
            const perms = stored[templateId] || [];
            if (selected) {
                nextModes[templateId] = "on";
                nextOverrides[templateId] = [...perms];
            } else {
                nextModes[templateId] = perms.length ? "custom" : "hidden";
                nextOverrides[templateId] = [...perms];
            }
        });
        setModes(nextModes);
        setOverrides(nextOverrides);
        loadTemplates(0, true);
    }, []);

    async function loadTemplates(offset: number, reset: boolean) {
        if (!reset && loadingMore) {
            return;
        }
        if (reset) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const {data} = await httpClient.get("/templates", {
                params: {limit: PAGE_SIZE, offset},
            });
            const chunk: TemplateRow[] = Array.isArray(data) ? data : (data?.templates || []);
            setTemplates((prev) => (reset ? chunk : [...prev, ...chunk]));
            setHasMoreTemplates(chunk.length === PAGE_SIZE);
        } catch (error) {
            notifyAboutError(props, error);
            setHasMoreTemplates(false);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }

    function setTemplateMode(templateId: string, mode: TemplateMode) {
        setModes({...modes, [templateId]: mode});
        if (
            (mode === "custom" || mode === "on")
            && !(overrides[templateId] || []).length
        ) {
            setOverrides({
                ...overrides,
                [templateId]: [...defaultPermissions],
            });
        }
    }

    function buildPayload() {
        const templatesMap: {[id: string]: string[]} = {};
        Object.keys(modes).forEach((templateId) => {
            const mode = modes[templateId];
            if (scope === "selected") {
                if (mode === "on") {
                    templatesMap[templateId] = overrides[templateId] || [];
                }
                return;
            }
            if (mode === "hidden") {
                templatesMap[templateId] = [];
            } else if (mode === "custom") {
                templatesMap[templateId] = overrides[templateId] || [];
            }
        });
        return {
            name: name.trim(),
            scope,
            default_permissions: defaultPermissions,
            templates: templatesMap,
        };
    }

    async function save() {
        if (!name.trim()) {
            notifyAboutError(props, new Error("Укажите название роли"));
            return;
        }
        setSaving(true);
        try {
            const payload = buildPayload();
            if (editing) {
                await httpClient.patch(`/access-profiles/${props.profile?._id}`, payload);
                notifyUser(props, "Роль обновлена");
            } else {
                await httpClient.post("/access-profiles", payload);
                notifyUser(props, "Роль создана");
            }
            props.onSaved();
        } catch (error) {
            notifyAboutError(props, error);
        } finally {
            setSaving(false);
        }
    }

    async function remove() {
        if (!props.profile?._id) {
            return;
        }
        setSaving(true);
        try {
            await httpClient.delete(`/access-profiles/${props.profile._id}`);
            notifyUser(props, "Роль удалена");
            props.onSaved();
        } catch (error) {
            notifyAboutError(props, error);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Popup maxWidth="720px" onClose={props.onClose}>
            <div style={{width: mobile ? "100%" : "560px"}}>
                <div className="commonTitle" style={{marginBottom: "10px"}}>
                    {editing ? "Редактирование роли" : "Новая роль доступа"}
                </div>
                <div className="commonSubtitle" style={{marginBottom: "16px"}}>
                    {scope === "selected"
                        ? "Сотрудник видит только выданные шаблоны. Новые сами не появятся."
                        : "Права по умолчанию действуют на все шаблоны, включая новые. Без базового доступа шаблоны скрыты — пока не зададите исключение «Свой набор»."}
                </div>
                {loading ? (
                    <PageLoader/>
                ) : (
                    <>
                        <div className="commonLabel" style={{marginBottom: "6px"}}>
                            Название
                        </div>
                        <input
                            className="ui-kit-v2"
                            style={{width: "100%", boxSizing: "border-box"}}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Например, Менеджер"
                        />

                        <div className="commonLabel" style={{marginTop: "18px", marginBottom: "6px"}}>
                            Режим
                        </div>
                        <div style={{display: "flex", flexWrap: "wrap", gap: "8px"}}>
                            <button
                                type="button"
                                className={`new action-button ${scope === "all" ? "primary" : "secondary"}`}
                                onClick={() => setScope("all")}
                            >
                                <span className="button-text">Все шаблоны</span>
                            </button>
                            <button
                                type="button"
                                className={`new action-button ${scope === "selected" ? "primary" : "secondary"}`}
                                onClick={() => setScope("selected")}
                            >
                                <span className="button-text">Только выбранные</span>
                            </button>
                        </div>

                        <div className="commonLabel" style={{marginTop: "18px", marginBottom: "6px"}}>
                            {scope === "selected" ? "Права при выдаче шаблона" : "Права по умолчанию"}
                        </div>
                        {permissionCheckboxes(defaultPermissions, (permissionId) => {
                            setDefaultPermissions(togglePermission(defaultPermissions, permissionId));
                        })}

                        <div className="commonLabel" style={{marginTop: "18px", marginBottom: "6px"}}>
                            {scope === "selected" ? "Шаблоны" : "Исключения по шаблонам"}
                        </div>
                        {templates.length === 0 && !hasMoreTemplates && (
                            <div className="commonSubtitle">Шаблонов пока нет</div>
                        )}
                        {templates.map((template) => {
                            const mode = modes[template._id] || (scope === "selected" ? "off" : "default");
                            return (
                                <div
                                    key={template._id}
                                    className="templateViewContainer disabledHover"
                                    style={{marginBottom: "12px"}}
                                >
                                    <div className="Subheader_17" style={{marginBottom: "8px"}}>
                                        {template.name || "Без названия"}
                                    </div>
                                    <div style={{display: "flex", flexWrap: "wrap", gap: "8px"}}>
                                        {(scope === "selected"
                                            ? [
                                                {id: "off", title: "Не выдан"},
                                                {id: "on", title: "Выдан"},
                                            ]
                                            : [
                                                {id: "default", title: "Как у роли"},
                                                {id: "custom", title: "Свой набор"},
                                                {id: "hidden", title: "Скрыть"},
                                            ]
                                        ).map((option) => (
                                            <button
                                                key={option.id}
                                                type="button"
                                                className={`new action-button ${mode === option.id ? "primary" : "secondary"}`}
                                                onClick={() => setTemplateMode(template._id, option.id as TemplateMode)}
                                            >
                                                <span className="button-text">{option.title}</span>
                                            </button>
                                        ))}
                                    </div>
                                    {(mode === "custom" || mode === "on") && permissionCheckboxes(
                                        overrides[template._id] || [],
                                        (permissionId) => {
                                            setOverrides({
                                                ...overrides,
                                                [template._id]: togglePermission(
                                                    overrides[template._id],
                                                    permissionId,
                                                ),
                                            });
                                        },
                                    )}
                                </div>
                            );
                        })}
                        {hasMoreTemplates && (
                            <button
                                className="new withTitle secondary _wa"
                                disabled={loadingMore}
                                onClick={() => loadTemplates(templates.length, false)}
                            >
                                {loadingMore ? "Загрузка…" : "Показать ещё"}
                            </button>
                        )}

                        <hr className="newHR"/>
                        <div className="buttonsContainer" style={{width: "100%"}}>
                            <button
                                className="new withTitle _wa"
                                disabled={saving}
                                onClick={save}
                            >
                                Сохранить
                            </button>
                            {editing && (
                                <button
                                    className="new withTitle secondary _wa"
                                    disabled={saving}
                                    onClick={remove}
                                    style={{color: "#D32F2F"}}
                                >
                                    Удалить
                                </button>
                            )}
                            <button
                                className="new withTitle secondary _wa"
                                onClick={props.onClose}
                            >
                                Отмена
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Popup>
    );
}

export default AccessProfilePopup;
